import { useEffect, useRef, useState } from "react";
import Shell from "@/components/Shell";
import api, { API, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Upload, RefreshCw, Sparkles, CheckCircle2, FlipHorizontal2, UserRound, Info, AlertTriangle } from "lucide-react";

// The certificate prints "Dr." before the name — keep the typed name clean.
const DR_PREFIX_RE = /^\s*(dr|doctor)\b\.?\s*/i;
const cleanDoctorName = (name) => name.replace(DR_PREFIX_RE, "").trim();
// Mirrors the backend's display casing: capitalize all-lower/all-upper words,
// keep mixed-case words (so "McGregor" survives).
const titleCase = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w === w.toLowerCase() || w === w.toUpperCase()
      ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
      : w))
    .join(" ");

export default function MRGenerate() {
  const [doctorName, setDoctorName] = useState("");
  const [speciality, setSpeciality] = useState("");
  const [ccCode, setCcCode] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoName, setPhotoName] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [previewRequest, setPreviewRequest] = useState(null);
  const [quota, setQuota] = useState({ used: 0, limit: 6, remaining: 6 });

  const [camOpen, setCamOpen] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadQuota = () => {
    api.get("/requests/quota")
      .then(({ data }) => setQuota(data))
      .catch(() => {});
  };

  useEffect(() => {
    loadQuota();
  }, []);

  const clearPreview = () => setPreviewRequest(null);

  const onFile = (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Please pick a PNG, JPG or WEBP image");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Image too large (max 20MB)");
      return;
    }
    setPhotoFile(file);
    setPhotoName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);
    clearPreview();
  };

  const startCamera = async (mode = "environment") => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    try {
      setCamOpen(true);
      setFacingMode(mode);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch {
      toast.error("Cannot access camera. Please allow camera permission.");
      setCamOpen(false);
    }
  };

  const switchCamera = () => startCamera(facingMode === "user" ? "environment" : "user");

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamOpen(false);
  };

  const captureCamera = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `doctor-${Date.now()}.jpg`, { type: "image/jpeg" });
      onFile(file);
      stopCamera();
      toast.success("Doctor photo captured");
    }, "image/jpeg", 0.92);
  };

  const generatePreview = async (e) => {
    e.preventDefault();
    if (!doctorName.trim()) { toast.error("Please enter the doctor's name"); return; }
    if (!speciality.trim()) { toast.error("Please enter the speciality"); return; }
    if (!photoFile) { toast.error("Please upload or capture the doctor's photo"); return; }
    setPreviewBusy(true);
    try {
      const fd = new FormData();
      fd.append("doctor_name", doctorName.trim());
      fd.append("speciality", speciality.trim());
      fd.append("cc_code", ccCode.trim());
      fd.append("mobile_number", mobileNumber.trim());
      fd.append("date_of_birth", dateOfBirth.trim());
      fd.append("photo", photoFile);
      const { data } = await api.post("/requests/draft", fd);
      setPreviewRequest(data);
      toast.success("Preview ready. Review it before submitting.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setPreviewBusy(false);
    }
  };

  const submitDraft = async () => {
    if (!previewRequest || previewRequest.status !== "draft") {
      toast.error("Generate a preview first.");
      return;
    }
    if (quota.remaining <= 0) {
      toast.error(`You have reached your ${quota.limit} request limit. Please ask admin to unlock your ID.`);
      return;
    }
    setSubmitBusy(true);
    try {
      const { data } = await api.post(`/requests/${previewRequest.id}/submit`);
      setPreviewRequest(data);
      toast.success(`Request ${data.display_id} submitted!`);
      loadQuota();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitBusy(false);
    }
  };

  const reset = () => {
    setPreviewRequest(null);
    setPhotoFile(null);
    setPhotoName("");
    setPhotoPreview(null);
    setDoctorName("");
    setSpeciality("");
    setCcCode("");
    setMobileNumber("");
    setDateOfBirth("");
  };

  return (
    <Shell role="mr">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Master of ICU</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Create a Master of ICU certificate</h1>
        <p className="text-slate-500 mt-1">Fill doctor details, generate a draft preview, review it, then submit the request.</p>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600">
          <span className="font-semibold">Request quota:</span>
          <span>{quota.used}/{quota.limit} used</span>
          <span className="text-slate-400">|</span>
          <span>{quota.remaining} left</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6 lg:gap-10">
        {/* Form */}
        <form onSubmit={generatePreview} className="space-y-5">
          <Card className="p-5 md:p-6 space-y-5">
            <Field label="Doctor Name *">
              <Input
                value={doctorName}
                onChange={(e) => { setDoctorName(e.target.value); clearPreview(); }}
                placeholder="Sapna Sawant"
                maxLength={100}
                required
              />
              {DR_PREFIX_RE.test(doctorName) && (
                <div className="text-xs text-amber-700 mt-1 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>No need to type "Dr." — the certificate already adds it. We'll drop it automatically.</span>
                </div>
              )}
              <div className="text-xs text-slate-400 mt-1">Shown on the certificate as "Dr. {titleCase(cleanDoctorName(doctorName)) || "Name"}".</div>
            </Field>

            <Field label="Speciality *">
              <Input
                value={speciality}
                onChange={(e) => { setSpeciality(e.target.value); clearPreview(); }}
                placeholder="Cardiology"
                required
              />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="CC Code">
                <Input
                  value={ccCode}
                  onChange={(e) => { setCcCode(e.target.value); clearPreview(); }}
                  placeholder="CC-102"
                />
              </Field>
              <Field label="Mobile Number">
                <Input
                  value={mobileNumber}
                  onChange={(e) => { setMobileNumber(e.target.value); clearPreview(); }}
                  placeholder="9876543210"
                  inputMode="tel"
                />
              </Field>
            </div>

            <Field label="Date of Birth">
              <Input
                type="date"
                value={dateOfBirth}
                onChange={(e) => { setDateOfBirth(e.target.value); clearPreview(); }}
              />
            </Field>
          </Card>

          {/* Doctor photo */}
          <Card className="p-5 md:p-6">
            <div className="mb-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Doctor's Photo</div>
              <div className="font-display font-bold text-slate-900 mt-1">Upload or capture</div>
            </div>

            <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-blue-50 border border-blue-100 text-blue-700">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="text-xs leading-relaxed">
                Use a clear, recent, front-facing photo of the doctor in good light,
                ideally against a <b>plain background</b>. The photo appears on the
                certificate <b>exactly as uploaded</b>, cropped to fit its frame.
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-stretch">
              <div className="flex-1 grid grid-cols-2 gap-3">
                <Button type="button" variant="outline" className="h-24 flex flex-col gap-2" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-5 h-5" />
                  <span className="text-sm font-semibold">Upload</span>
                </Button>
                <Button type="button" variant="outline" className="h-24 flex flex-col gap-2" onClick={() => startCamera("environment")}>
                  <Camera className="w-5 h-5" />
                  <span className="text-sm font-semibold">Camera</span>
                </Button>
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              </div>
              <div className="w-full sm:w-40 h-40 rounded-lg border border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden">
                {photoPreview ? (
                  <img src={photoPreview} alt="Doctor photo" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-xs text-slate-400 text-center px-2 flex flex-col items-center gap-1">
                    <UserRound className="w-6 h-6 text-slate-300" />
                    No photo yet
                  </div>
                )}
              </div>
            </div>

          </Card>

          {/* Inline certificate preview — shown on mobile only when a preview exists */}
          {previewRequest && (
            <div className="lg:hidden">
              <Card className="p-3 canvas-checker">
                <div className="bg-white rounded-md p-2">
                  <img
                    src={`${API}/requests/${previewRequest.id}/preview?t=${Date.now()}`}
                    alt="Draft certificate preview"
                    className="w-full rounded"
                  />
                </div>
              </Card>
            </div>
          )}

          {previewRequest && previewRequest.status === "draft" ? (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                <div>
                  <div className="font-semibold">Preview generated — does it look correct?</div>
                  <div className="text-amber-700 text-xs mt-0.5">Click <b>Submit Request</b> below to officially send it. The preview is <b>not submitted yet</b>.</div>
                </div>
              </div>
              <Button type="button" onClick={submitDraft} disabled={submitBusy || quota.remaining <= 0} className="h-12 w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 text-base font-semibold">
                {submitBusy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Submitting...</> : <><CheckCircle2 className="w-4 h-4" /> Submit Request</>}
              </Button>
              <div className="flex gap-3">
                <Button type="submit" variant="outline" disabled={previewBusy || !photoFile} className="h-10 flex-1 gap-2 text-sm">
                  {previewBusy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Regenerating...</> : <><RefreshCw className="w-4 h-4" /> Regenerate Preview</>}
                </Button>
                <Button type="button" variant="outline" className="h-10" onClick={reset}>New Certificate</Button>
              </div>
              <p className="text-xs text-amber-700 text-center">Note: Resubmission is allowed for only 2 attempts.</p>
            </div>
          ) : previewRequest && previewRequest.status !== "draft" ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <div>
                  <div className="font-semibold">Request {previewRequest.display_id} submitted!</div>
                  <div className="text-xs text-emerald-600/80">The admin team will print, frame and dispatch this certificate.</div>
                </div>
              </div>
              <Button type="button" variant="outline" className="h-12 w-full" onClick={reset}>
                Start New Certificate
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Button type="submit" disabled={previewBusy || !photoFile} className="h-12 w-full bg-brand-800 hover:bg-brand-700 gap-2">
                {previewBusy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating preview...</> : <><Sparkles className="w-4 h-4" /> Generate Preview</>}
              </Button>
              <p className="text-xs text-slate-400 text-center">A draft preview will be generated first. You can review it before submitting.</p>
            </div>
          )}
        </form>

        {/* Preview — desktop right column */}
        <div className="hidden lg:block lg:sticky lg:top-6 self-start">
          <Card className="p-3 canvas-checker">
            <div className="bg-white rounded-md p-2">
              {previewRequest ? (
                <img
                  src={`${API}/requests/${previewRequest.id}/preview?t=${Date.now()}`}
                  alt="Draft certificate preview"
                  className="w-full rounded"
                />
              ) : (
                <div className="aspect-[3451/2401] w-full rounded grid place-items-center bg-slate-50 text-slate-300">
                  <div className="text-center">
                    <UserRound className="w-10 h-10 mx-auto mb-2" />
                    <div className="text-sm">Certificate preview appears here</div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {previewRequest && (
            <div className="mt-4 flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold">
                  {previewRequest.status === "draft"
                    ? `Draft ready for review`
                    : `Request ${previewRequest.display_id} submitted`}
                </div>
                <div className="text-emerald-600/80">
                  {previewRequest.status === "draft"
                    ? "Review the certificate preview and submit it when it looks right."
                    : "The admin team will print, frame and dispatch this certificate. You can track its status in your Dashboard."}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Camera dialog */}
      <Dialog open={camOpen} onOpenChange={(o) => (o ? null : stopCamera())}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Capture Doctor's Photo</DialogTitle>
          </DialogHeader>
          <div className="aspect-[3/4] bg-black rounded-md overflow-hidden relative">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            <button
              type="button"
              onClick={switchCamera}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white grid place-items-center transition"
              title={facingMode === "user" ? "Switch to back camera" : "Switch to front camera"}
            >
              <FlipHorizontal2 className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-3">
            <Button onClick={captureCamera} className="flex-1 bg-brand-800">
              <Camera className="w-4 h-4 mr-2" />Capture
            </Button>
            <Button variant="outline" onClick={stopCamera}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    {children}
  </div>
);
